import {
  Box,
  Button,
  Grid,
  HStack,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { algoliasearch, type SearchClient } from "algoliasearch";
import actions from "src/actions";
import StatCard from "../Components/StatCard";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import type { CampaignIntent } from "common/interfaces/KYDMarketing";
import type { CampaignMetricsSnapshot } from "src/actions/ads";
import { orgMetricsToDisplay } from "./AdsEngagePage/utils";
import type { AdCampaignSortOption } from "./AdsEngagePage/utils";
import CampaignsTableAlgolia from "./AdsEngagePage/CampaignsTableAlgolia";
import CreateCampaignFlowModal, {
  type CreateCampaignFlowDraft,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import GuidedCreateCampaignFlowScreen from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedCreateCampaignFlowScreen";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import { launchCampaignWithPolling } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/guidedCampaignLaunch";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import ProactiveInsightsSection from "./AdsEngagePage/ProactiveInsightsSection";

export default function AdsEngagePage() {
  const toast = useToast();
  const [orgMetrics, setOrgMetrics] = useState<CampaignMetricsSnapshot | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [algoliaConfig, setAlgoliaConfig] = useState<{
    searchClient: SearchClient;
    indexName: string;
  } | null>(null);
  const [algoliaFailed, setAlgoliaFailed] = useState(false);
  const [sortOrderActive, setSortOrderActive] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [sortOrderPast, setSortOrderPast] = useState<AdCampaignSortOption>(
    "updated_at_descending"
  );
  const [adAccounts, setAdAccounts] = useState<{ id: string; name: string }[]>(
    []
  );
  const [createCampaignFlowDraft, setCreateCampaignFlowDraft] =
    useState<CreateCampaignFlowDraft | null>(null);
  const [isCreateCampaignFlowModalOpen, setIsCreateCampaignFlowModalOpen] =
    useState(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [pixels, setPixels] = useState<{ id: string; name: string }[]>([]);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);
  const [guidedPublishStatus, setGuidedPublishStatus] = useState<string | null>(
    null
  );
  const [suggestedIntents, setSuggestedIntents] = useState<CampaignIntent[]>(
    []
  );

  const nowUnix = Math.floor(Date.now() / 1000);
  // First table: current/upcoming campaigns.
  const numericFiltersNotPast: (string | string[])[] = [
    ["schedule_end_at_unix = 0", `schedule_end_at_unix >= ${nowUnix}`],
  ];
  const numericFiltersPast: (string | string[])[] = [
    "schedule_end_at_unix > 0",
    `schedule_end_at_unix < ${nowUnix}`,
  ];

  const { pollForResult } = useManagedCampaigns({
    defaultCampaign: null,
    defaultEditingAdset: null,
    pages,
    instagramAccounts,
    onPollingComplete: () => {},
  });

  const refetchRelated = useCallback(async (ad_account_id: string) => {
    setRelatedDataLoading(true);
    try {
      await Promise.all([
        actions.ADMIN.listPages({ ad_account_id }).then(setPages),
        actions.ADMIN.listPixels({ ad_account_id }).then(setPixels),
        actions.ADMIN.listInstagramAccounts({ ad_account_id }).then(
          setInstagramAccounts
        ),
      ]);
    } catch (e) {
      console.error("Error fetching related data:", e);
    } finally {
      setRelatedDataLoading(false);
    }
  }, []);

  const refetchInstagramForPage = useCallback(
    (facebookPageId: string) => {
      if (!createCampaignFlowDraft?.adAccountId) return;
      setRelatedDataLoading(true);
      actions.ADMIN.listInstagramAccounts({
        ad_account_id: createCampaignFlowDraft.adAccountId,
        facebook_page_id: facebookPageId,
      })
        .then(setInstagramAccounts)
        .catch((e) => console.error("Error fetching Instagram accounts:", e))
        .finally(() => setRelatedDataLoading(false));
    },
    [createCampaignFlowDraft?.adAccountId]
  );

  const handleFlowCreateCampaign = useCallback(
    async (
      params: Parameters<
        import("src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/CreateCampaignFlow").CreateCampaignFlowProps["handleCreateCampaign"]
      >[0]
    ) => {
      await launchCampaignWithPolling(params, {
        toast,
        pollForResult,
        onStatusUpdate: setGuidedPublishStatus,
        onDone: () => setGuidedPublishStatus(null),
      });
    },
    [pollForResult, toast]
  );

  useEffect(() => {
    actions.ADMIN.listAdAccounts?.()
      .then((r: any) => {
        const list = r?.accounts ?? r ?? [];
        setAdAccounts(Array.isArray(list) ? list : []);
      })
      .catch(() => setAdAccounts([]));
  }, []);

  useEffect(() => {
    if (createCampaignFlowDraft?.adAccountId) {
      refetchRelated(createCampaignFlowDraft.adAccountId);
    }
  }, [createCampaignFlowDraft?.adAccountId, refetchRelated]);

  const refetchOrgMetrics = useCallback(async () => {
    try {
      const res = await actions.ADMIN.fetchOrganizationAdsMetrics();
      setOrgMetrics(res.metrics ?? null);
      setSuggestedIntents(res.suggested_intents ?? []);
    } catch {
      // keep previous state
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    actions.ADMIN.fetchOrganizationAdsMetrics()
      .then((res) => {
        if (cancelled) return;
        setOrgMetrics(res?.metrics ?? null);
        setSuggestedIntents(res?.suggested_intents ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (algoliaFailed) return;
    let cancelled = false;
    actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.AD_CAMPAIGNS,
    })
      .then((res) => {
        if (cancelled || !res?.events_config) return;
        const search = algoliasearch(
          res.events_config.app_id,
          res.events_config.api_key
        );
        setAlgoliaConfig({
          searchClient: search,
          indexName: res.events_config.index_name,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAlgoliaFailed(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [algoliaFailed]);

  const metricsFromOrg = useMemo(
    () => orgMetricsToDisplay(orgMetrics),
    [orgMetrics]
  );
  const metrics = useMemo(
    () =>
      metricsFromOrg ?? {
        displaySpend: "$0",
        displayRoas: "-",
        displayTickets: "0",
        displayCtr: "-",
      },
    [metricsFromOrg]
  );

  if (createCampaignFlowDraft) {
    return (
      <GuidedCreateCampaignFlowScreen
        draft={createCampaignFlowDraft}
        adAccounts={adAccounts}
        pages={pages}
        instagramAccounts={instagramAccounts}
        pixels={pixels}
        isRelatedDataLoading={relatedDataLoading}
        createUploadUrl={actions.ADMIN.createPublicUploadUrl}
        onBack={() => {
          setGuidedPublishStatus(null);
          setCreateCampaignFlowDraft(null);
        }}
        onLaunchSuccess={() => {
          setGuidedPublishStatus(null);
          setCreateCampaignFlowDraft(null);
        }}
        toast={toast}
        onRefetchInstagramForPage={refetchInstagramForPage}
        handleCreateCampaign={handleFlowCreateCampaign}
        publishStatusText={guidedPublishStatus}
      />
    );
  }

  return (
    <VStack w="100%" align="stretch" spacing={6} py={4}>
      <HStack justifyContent="space-between" w="100%" flexWrap="wrap" gap={3}>
        <Text fontSize="32px" fontWeight="400">
          Meta Campaigns
        </Text>
        <Button
          leftIcon={<FiPlus />}
          size="md"
          bg="black"
          color="white"
          borderRadius="full"
          _hover={{ bg: "gray.800" }}
          onClick={() => setIsCreateCampaignFlowModalOpen(true)}
        >
          New Ad Campaign
        </Button>
      </HStack>

      <Grid
        templateColumns={{
          base: "1fr 1fr",
          md: "repeat(4, 1fr)",
        }}
        gap={4}
      >
        <StatCard
          title="Spend (last 30 days)"
          value={metrics.displaySpend}
          valueFontSize="20px"
        />
        <StatCard
          title="Avg. ROAS"
          value={metrics.displayRoas}
          valueFontSize="20px"
        />
        <StatCard
          title="Checkouts"
          value={metrics.displayTickets}
          valueFontSize="20px"
          isKYD
          highlight
        />
        <StatCard
          title="Avg. CTR"
          value={metrics.displayCtr}
          valueFontSize="20px"
        />
      </Grid>

      <ProactiveInsightsSection
        intents={suggestedIntents}
        onRefetch={refetchOrgMetrics}
      />

      <Box>
        {loading ? (
          <VStack py={6}>
            <Spinner />
          </VStack>
        ) : algoliaFailed ? (
          <Box
            p={4}
            borderWidth="1px"
            borderRadius="md"
            borderColor="red.200"
            bg="red.50"
          >
            <Text color="red.700" fontSize="sm">
              Search is temporarily unavailable. Please try again later.
            </Text>
          </Box>
        ) : !algoliaConfig ? (
          <VStack py={6}>
            <Spinner />
            <Text color="gray.500" fontSize="sm">
              Setting up search…
            </Text>
          </VStack>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Current & upcoming campaigns"
            sortOrder={sortOrderActive}
            onSortChange={setSortOrderActive}
            numericFilters={numericFiltersNotPast}
          />
        )}
      </Box>

      <Box>
        {loading || algoliaFailed || !algoliaConfig ? (
          <Text color="gray.500" fontSize="sm" py={2}>
            {algoliaFailed
              ? "Search unavailable."
              : !algoliaConfig
                ? "Setting up search…"
                : "Loading…"}
          </Text>
        ) : (
          <CampaignsTableAlgolia
            searchClient={algoliaConfig.searchClient}
            indexName={algoliaConfig.indexName}
            title="Past Campaigns"
            sortOrder={sortOrderPast}
            onSortChange={setSortOrderPast}
            numericFilters={numericFiltersPast}
            showPerformanceIndicator={false}
            showLegend={false}
          />
        )}
      </Box>
      <CreateCampaignFlowModal
        isOpen={isCreateCampaignFlowModalOpen}
        onClose={() => setIsCreateCampaignFlowModalOpen(false)}
        adAccounts={adAccounts}
        onDraftReady={(draft) => {
          setCreateCampaignFlowDraft(draft);
          setIsCreateCampaignFlowModalOpen(false);
        }}
      />
    </VStack>
  );
}
